/**
 * Features Page
 */

import React from 'react';
import { 
  Shield, Brain, Heart, Clock, RefreshCw, Users, 
  Lock, Eye, MessageCircle, Sparkles, CheckCircle
} from 'lucide-react';

const MAIN_FEATURES = [
  {
    icon: Brain,
    title: 'ذكاء اصطناعي متقدم',
    description: 'خوارزميات متطورة تحلل شخصيتك وتفضيلاتك لإيجاد أفضل توافق',
    color: 'purple',
    details: [
      'تحليل عميق للشخصية',
      'مطابقة ذكية بناءً على القيم',
      'اقتراحات مواضيع للمحادثة',
      'تطوير مستمر للخوارزميات'
    ]
  },
  {
    icon: Shield,
    title: 'خصوصية مطلقة',
    description: 'بياناتك مشفرة بأعلى معايير الأمان ولا يطّلع عليها أحد',
    color: 'green',
    details: [
      'تشفير AES-256',
      'بياناتك لا تُباع أبداً',
      'تحكم كامل بمعلوماتك',
      'حذف فوري عند الطلب'
    ]
  },
  {
    icon: Heart,
    title: 'توافق واحد في كل مرة',
    description: 'نؤمن أن التعارف الجاد يحتاج تركيز، لذلك نعرض لك شخص واحد',
    color: 'rose',
    details: [
      'تركيز بدون تشتت',
      'وقت كافي للتعارف',
      'قرارات مدروسة',
      'جدية أكثر من الطرفين'
    ]
  },
  {
    icon: Clock,
    title: '7 أيام للتعارف',
    description: 'فترة كافية للتعرف على الطرف الآخر قبل اتخاذ القرار',
    color: 'blue',
    details: [
      'وقت مناسب للحوار',
      'قابل للتمديد 4 مرات',
      'إشعار قبل الانتهاء',
      'مرونة في القرار'
    ]
  },
  {
    icon: RefreshCw,
    title: 'مرونة كاملة',
    description: 'تقدر تجدد أو تنسحب بأي لحظة بدون أي إحراج',
    color: 'amber',
    details: [
      'تمديد حتى 35 يوم',
      'انسحاب محترم',
      'رسائل لطيفة تلقائية',
      'بدون ضغط أو إحراج'
    ]
  },
  {
    icon: Users,
    title: 'للسعوديين فقط',
    description: 'مجتمع سعودي يفهم ثقافتنا وعاداتنا ويحترمها',
    color: 'emerald',
    details: [
      'تحقق من الجنسية',
      'أسئلة تناسب مجتمعنا',
      'فهم للعادات والتقاليد',
      'دعم باللغة العربية'
    ]
  }
];

const SECURITY_FEATURES = [
  { icon: Lock, text: 'تشفير البيانات الحساسة' },
  { icon: Eye, text: 'لا نطّلع على محادثاتك' },
  { icon: Shield, text: 'حماية من الاختراق' },
  { icon: CheckCircle, text: 'توثيق البريد الإلكتروني' },
];

export default function Features() {
  const getColorClasses = (color) => {
    const colors = {
      purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
      green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
      rose: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' },
      blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
      amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
      emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
    };
    return colors[color] || colors.purple;
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="bg-gradient-to-br from-rose-50 via-purple-50 to-pink-50 py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg mb-6">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-800">مميزات خالتي هيلة</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            ليش تختار خالتي هيلة؟
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            لأنها تجمع بين الأصالة والتقنية، وتهتم بتفاصيلك عشان تلاقي لك الشخص المناسب
          </p>
        </div>
      </section>

      {/* Main Features */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {MAIN_FEATURES.map((feature, index) => {
              const colors = getColorClasses(feature.color);
              const Icon = feature.icon;
              
              return (
                <div 
                  key={index}
                  className={`rounded-2xl p-6 md:p-8 border-2 ${colors.border} hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}
                >
                  <div className={`w-14 h-14 ${colors.bg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon className={`w-7 h-7 ${colors.text}`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 mb-4">{feature.description}</p>
                  <ul className="space-y-2">
                    {feature.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className={`w-4 h-4 ${colors.text}`} />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <Shield className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h2 className="text-2xl md:text-4xl font-bold mb-4">أمانك أولويتنا</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              نستخدم أحدث تقنيات الحماية عشان تكون بياناتك في أمان تام
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {SECURITY_FEATURES.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
                  <Icon className="w-10 h-10 text-green-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-300">{feature.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Chat Feature */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <MessageCircle className="w-12 h-12 text-rose-500 mb-4" />
              <h2 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">
                محادثات آمنة وذكية
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                خالتي هيلة توفر لك بيئة محادثة آمنة ومحترمة، مع اقتراحات ذكية للمواضيع اللي تناسبكم
              </p>
              <ul className="space-y-3">
                {[
                  'اقتراحات مواضيع من الذكاء الاصطناعي',
                  'مؤشر "يكتب الآن..."',
                  'تأكيد قراءة الرسائل ✓✓',
                  'إمكانية الإبلاغ والحظر',
                  'رسائل مشفرة بالكامل'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-rose-100 to-purple-100 rounded-3xl p-8">
              <div className="bg-white rounded-2xl shadow-xl p-4 space-y-3">
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl rounded-tr-none px-4 py-2 max-w-[80%]">
                    <p className="text-gray-800">السلام عليكم، كيف حالك؟ 😊</p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <div className="bg-gradient-to-r from-rose-500 to-purple-500 text-white rounded-2xl rounded-tl-none px-4 py-2 max-w-[80%]">
                    <p>وعليكم السلام، الحمدلله بخير! وأنت؟</p>
                  </div>
                </div>
                <div className="text-center">
                  <span className="text-xs text-gray-400">💡 اقتراح: اسأل عن الهوايات المشتركة</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
